import { ROUTES_NAME } from "../../../config/constants";
import { ACCESS_TOKEN } from "../../../constants";
import { authenticatedFetch } from "../../../util/authenticatedFetch";
import {
  HISTORICAL_QUEUE_ERROR,
  HISTORICAL_QUEUE_REQUEST,
  HISTORICAL_QUEUE_SUCCESS,
  HISTORICAL_STATUS_ERROR,
  HISTORICAL_STATUS_REQUEST,
  HISTORICAL_STATUS_SUCCESS,
  HISTORICAL_TOKEN_ERROR,
  HISTORICAL_TOKEN_REQUEST,
  HISTORICAL_TOKEN_SUCCESS,
  HISTORICAL_TOTAL_POSSIBLE_ORDERS_REQUEST,
  HISTORICAL_TOTAL_POSSIBLE_ORDERS_TOKEN_ERROR,
  HISTORICAL_TOTAL_POSSIBLE_ORDERS_TOKEN_SUCCESS,
} from "./ActionType";

let requestSequence = 0;

const normalizeShopId = (shopId) => String(shopId ?? "").trim();

const requestMeta = (operation, shopId, extra = {}) => ({
  shopId: normalizeShopId(shopId),
  requestId: `${operation}:${normalizeShopId(shopId)}:${Date.now()}:${++requestSequence}`,
  ...extra,
});

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem(ACCESS_TOKEN)}`,
  "Content-Type": "application/json",
  Origin: `${process.env.REACT_APP_URL}`,
});

const readResponsePayload = async (response) => {
  if (typeof response?.text === "function") {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (_error) {
      return text;
    }
  }
  if (typeof response?.json === "function") {
    return response.json();
  }
  return null;
};

const responseError = (payload, response, fallback) => {
  const message =
    (typeof payload === "string" && payload.trim() && payload) ||
    [payload?.message, payload?.error, payload?.detail].find(
      (value) => typeof value === "string" && value.trim()
    );
  return message || `${fallback} (${response?.status || "unknown status"})`;
};

const errorText = (error, fallback) =>
  typeof error?.message === "string" && error.message.trim()
    ? error.message
    : fallback;

const requireCount = (payload, errorMessage) => {
  const count = payload?.count;
  if (
    typeof count !== "number" ||
    !Number.isSafeInteger(count) ||
    count < 0
  ) {
    throw new Error(errorMessage);
  }
  return count;
};

const validateShopId = (shopId) => {
  if (!shopId) throw new Error("A store ID is required.");
};

const validateDate = (date) => {
  const value = String(date ?? "");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new Error("A valid start date is required.");
  }
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    throw new Error("A valid start date is required.");
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (parsed > today) {
    throw new Error("The start date cannot be in the future.");
  }
};

const postJson = async (url, body) => {
  const response = await authenticatedFetch(url, {
    method: "POST",
    body: JSON.stringify(body),
    credentials: "include",
    headers: headers(),
  });
  const payload = await readResponsePayload(response);
  return { response, payload };
};

export const historicalOrdersStatus = ({ page = 0, shopId }) => async (dispatch) => {
  const meta = requestMeta("historicalStatus", shopId);
  dispatch({ type: HISTORICAL_STATUS_REQUEST, meta });

  try {
    validateShopId(meta.shopId);
    const url = `${process.env.REACT_APP_BACKEND_API_URL}${ROUTES_NAME.historicalStatus(meta.shopId)}`;
    const { response, payload } = await postJson(url, {
      pageNumber: page,
      length: 10,
    });
    if (!response?.ok) {
      throw new Error(responseError(payload, response, "Historical-request history failed"));
    }
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      throw new Error("Historical-request history returned an invalid response.");
    }
    dispatch({ type: HISTORICAL_STATUS_SUCCESS, payload, meta });
    return { ok: true, data: payload, meta };
  } catch (error) {
    const message = errorText(error, "Historical-request history failed");
    dispatch({ type: HISTORICAL_STATUS_ERROR, payload: message, error: true, meta });
    return { ok: false, error: message, meta };
  }
};

export const historicalOrdersQueue = ({ date, id, shopId, ordersCount, type = "USER" }) =>
  async (dispatch) => {
    const normalizedType = String(type ?? "").trim();
    const count = Number(ordersCount);
    const target = Object.freeze({
      shopId: normalizeShopId(shopId ?? id),
      date: String(date ?? ""),
      ordersCount: count,
      type: normalizedType,
    });
    const meta = requestMeta("historicalQueue", target.shopId, { target });
    dispatch({ type: HISTORICAL_QUEUE_REQUEST, meta });

    try {
      validateShopId(target.shopId);
      validateDate(target.date);
      if (target.type !== "USER") {
        throw new Error("This historical-order flow only creates USER requests.");
      }
      if (!Number.isSafeInteger(count) || count <= 0) {
        throw new Error("A valid eligible-order count is required.");
      }

      const url = `${process.env.REACT_APP_BACKEND_API_URL}${ROUTES_NAME.storeQueue(target.shopId)}`;
      const { response, payload } = await postJson(url, {
        backInTimeType: "USER",
        date: target.date,
        ordersCount: target.ordersCount,
      });
      if (!response?.ok) {
        throw new Error(responseError(payload, response, "Historical request failed"));
      }
      if (payload !== true) {
        throw new Error("PalSync did not confirm that the historical request was created.");
      }
      dispatch({ type: HISTORICAL_QUEUE_SUCCESS, payload: true, meta });
      return { ok: true, data: true, meta };
    } catch (error) {
      const message = errorText(error, "Historical request failed");
      dispatch({ type: HISTORICAL_QUEUE_ERROR, payload: message, error: true, meta });
      return { ok: false, error: message, meta };
    }
  };

export const historicalOrdersTokenCount = ({ date, shopId }) => async (dispatch) => {
  const meta = requestMeta("historicalToken", shopId);
  dispatch({ type: HISTORICAL_TOKEN_REQUEST, meta });

  try {
    validateShopId(meta.shopId);
    validateDate(date);
    const url = `${process.env.REACT_APP_BACKEND_API_URL}${ROUTES_NAME.historicalCount(meta.shopId)}`;
    const { response, payload } = await postJson(url, {
      backInTimeType: "USER",
      date,
    });
    if (!response?.ok) {
      throw new Error(responseError(payload, response, "Historical-token check failed"));
    }
    const count = requireCount(
      payload,
      "Historical-token check returned an invalid count."
    );
    const data = { ...payload, count };
    dispatch({ type: HISTORICAL_TOKEN_SUCCESS, payload: data, meta });
    return { ok: true, data, meta };
  } catch (error) {
    const message = errorText(error, "Historical-token check failed");
    dispatch({ type: HISTORICAL_TOKEN_ERROR, payload: message, error: true, meta });
    return { ok: false, error: message, meta };
  }
};

export const gettotalPossibleOrders = ({ date, shopId }) => async (dispatch) => {
  const meta = requestMeta("historicalPreview", shopId, { date: String(date ?? "") });
  dispatch({ type: HISTORICAL_TOTAL_POSSIBLE_ORDERS_REQUEST, meta });

  try {
    validateShopId(meta.shopId);
    validateDate(date);
    const url = `${process.env.REACT_APP_BACKEND_API_URL}${ROUTES_NAME.possible_orders(meta.shopId)}`;
    const { response, payload } = await postJson(url, {
      backInTimeType: "USER",
      date,
    });
    if (!response?.ok) {
      throw new Error(responseError(payload, response, "Eligible-order check failed"));
    }
    const count = requireCount(
      payload,
      "Eligible-order check returned an invalid count."
    );
    const data = { ...payload, count };
    dispatch({
      type: HISTORICAL_TOTAL_POSSIBLE_ORDERS_TOKEN_SUCCESS,
      payload: data,
      meta,
    });
    return { ok: true, data, meta };
  } catch (error) {
    const message = errorText(error, "Eligible-order check failed");
    dispatch({
      type: HISTORICAL_TOTAL_POSSIBLE_ORDERS_TOKEN_ERROR,
      payload: message,
      error: true,
      meta,
    });
    return { ok: false, error: message, meta };
  }
};
